export type TutorialCategory =
  | "overview"
  | "organizacao"
  | "workspaces"
  | "aprovacoes"
  | "conteudo";

export interface TutorialCategoryDefinition {
  value: TutorialCategory;
  label: string;
  keywords: string[];
}

export const tutorialCategories: TutorialCategoryDefinition[] = [
  {
    value: "overview",
    label: "Visão geral",
    keywords: ["dashboard", "indicadores", "resumo"],
  },
  {
    value: "organizacao",
    label: "Organização",
    keywords: ["colaboradores", "convites", "equipe"],
  },
  {
    value: "workspaces",
    label: "Workspaces",
    keywords: ["configuracao", "ambiente", "estrutura"],
  },
  {
    value: "aprovacoes",
    label: "Aprovações",
    keywords: ["fluxo", "processos", "validacao"],
  },
  {
    value: "conteudo",
    label: "Conteúdo",
    keywords: ["edicao", "alteracoes", "materiais"],
  },
];

export const tutorialCategoryMap = new Map<TutorialCategory, TutorialCategoryDefinition>(
  tutorialCategories.map((category) => [category.value, category]),
);

export interface TutorialVideo {
  id: string;
  title: string;
  description: string;
  shareUrl: string;
  embedUrl: string;
  categories: TutorialCategory[];
  keywords: string[];
}

const tutorialVideoSeed: Array<Omit<TutorialVideo, "embedUrl">> = [
  {
    id: "overview",
    title: "Visão geral",
    description: "Conheça os principais indicadores e componentes da visão geral do Thumbz.",
    shareUrl: "https://www.loom.com/share/1be252bccb29432b89e0e5b5b92936c8",
    categories: ["overview"],
    keywords: ["introducao", "indicadores", "painel"],
  },
  {
    id: "invite-team",
    title: "Convidar equipe",
    description: "Aprenda como convidar e gerenciar novos colaboradores para a organização.",
    shareUrl: "https://www.loom.com/share/5bf5fca0bbea45b5a5a9f19b91a67dab",
    categories: ["organizacao"],
    keywords: ["usuarios", "convite", "membros"],
  },
  {
    id: "create-workspace",
    title: "Criando workspace",
    description: "Descubra como criar um novo workspace do zero e configurá-lo para sua equipe.",
    shareUrl: "https://www.loom.com/share/ae2f0ac50806446da460836238444532",
    categories: ["workspaces"],
    keywords: ["novo", "setup", "ambiente"],
  },
  {
    id: "workspace-overview",
    title: "Explicação do workspace",
    description: "Veja em detalhes cada aba e funcionalidade disponível dentro do workspace.",
    shareUrl: "https://www.loom.com/share/d266e10da0b54001b615c29f299202b3",
    categories: ["workspaces"],
    keywords: ["abas", "detalhes", "navegacao"],
  },
  {
    id: "approval-model",
    title: "Modelo de aprovação",
    description: "Configure os modelos de aprovação e organize as etapas do seu fluxo.",
    shareUrl: "https://www.loom.com/share/7ec0147bb9ce4a8eb2858a4c25969252",
    categories: ["aprovacoes"],
    keywords: ["modelo", "fluxo", "estrutura"],
  },
  {
    id: "add-approver",
    title: "Cadastrar aprovador",
    description: "Aprenda como cadastrar e atualizar os aprovadores responsáveis por cada etapa.",
    shareUrl: "https://www.loom.com/share/c27d3c4d9593443bac37237a6c625004",
    categories: ["aprovacoes", "organizacao"],
    keywords: ["responsaveis", "permissoes", "etapas"],
  },
  {
    id: "create-approval",
    title: "Criando aprovação",
    description: "Saiba como iniciar uma nova aprovação utilizando modelos e participantes.",
    shareUrl: "https://www.loom.com/share/39bf0c837cf8487c8578f72731d0bcd2",
    categories: ["aprovacoes"],
    keywords: ["nova", "solicitacao", "processo"],
  },
  {
    id: "approving",
    title: "Aprovando (parte 1)",
    description: "Entenda como revisar, aprovar ou reprovar solicitações em andamento.",
    shareUrl: "https://www.loom.com/share/eeaf51d9ee574c82be563564bca32b63",
    categories: ["aprovacoes"],
    keywords: ["analise", "decisao", "andamento"],
  },
  {
    id: "approving-advanced",
    title: "Aprovando (parte 2)",
    description: "Veja recursos avançados durante o processo de aprovação e histórico.",
    shareUrl: "https://www.loom.com/share/9f62ab77193148d0af691419931f94f1",
    categories: ["aprovacoes"],
    keywords: ["avancado", "historico", "filtros"],
  },
  {
    id: "content-change",
    title: "Alteração de conteúdo",
    description: "Aprenda a solicitar e registrar alterações de conteúdo em aprovações em curso.",
    shareUrl: "https://www.loom.com/share/ffe600a2ee04458aa289f643aa028540",
    categories: ["conteudo", "aprovacoes"],
    keywords: ["edicao", "ajustes", "materiais"],
  },
];

export const tutorialVideos: TutorialVideo[] = tutorialVideoSeed.map((video) => ({
  ...video,
  embedUrl: video.shareUrl.replace("/share/", "/embed/"),
}));

export const tutorialVideoById = tutorialVideos.reduce<Record<string, TutorialVideo>>(
  (accumulator, video) => {
    const map = accumulator;
    map[video.id] = video;
    return map;
  },
  {},
);
